/**
 * Tax HTTP request schemas — SCRUM-213 (DS-012 Taxes & Fees BC §5, §5.6).
 *
 * Wire source of truth for the operator-facing Tax CRUD endpoints. Shapes
 * mirror the SCRUM-208 `Tax` aggregate input: `rate` is a `Percentage | Fixed`
 * discriminated union (no Compound — §5.2), `applicability` is manual-only with
 * a non-empty unit set (§5.5), `taxCategory` is free text or null (§5.2a), and
 * the PRO fields (`remitRentTaxToOwner` / `exemptionThresholdDays`) are stored
 * as given (§13.4/§13.5). Domain write-rejections (rate ≥ 0, exemption ≥ 1) are
 * re-asserted by the aggregate as defense-in-depth.
 *
 * `.strict()` everywhere: an unknown key is a malformed body → 422 (the global
 * `ZodValidationPipe`), never a silently-ignored field.
 */
import { z } from 'zod';
import { TAX_CADENCE_VALUES, TAX_RESOLUTION_MODE_VALUES, TAX_TEXT_MAX_LENGTH, REVENUE_DISTRIBUTION_METHOD_VALUES, RESERVATION_TYPE_INTERNAL_KEY_VALUES, } from './enums.js';
import { boundedText } from './_text.js';
const taxText = boundedText(TAX_TEXT_MAX_LENGTH);
/** A non-empty array of trimmed, non-blank opaque id strings. */
const idList = z.array(z.string().trim().min(1));
/** `rate` — Percentage XOR Fixed (mirrors the aggregate `TaxRate` union). */
const taxRateSchema = z.discriminatedUnion('computeForm', [
    z
        .object({
        computeForm: z.literal('Percentage'),
        ratePercentBasisPoints: z.number().int().min(0).max(10000),
    })
        .strict(),
    z
        .object({
        computeForm: z.literal('Fixed'),
        rateFlatCents: z.number().int().min(0),
        appliesPer: z.enum(TAX_CADENCE_VALUES),
    })
        .strict(),
]);
/** `applicability` — Lite manual-only, non-empty unit set (§5.5).
 *
 * SCRUM-272 v1.1: `reservationTypeAllowlist` is internalKey-native — a
 * `z.enum` over the shared blueprint key-set (DS-020 §4.2/§11), so an unknown
 * key is rejected at the HTTP boundary (422) before it can reach the
 * `TaxApplicability` VO write chokepoint. `null` = applies to all types. */
const taxApplicabilitySchema = z
    .object({
    resolutionMode: z.enum(TAX_RESOLUTION_MODE_VALUES),
    unitAssignments: idList.min(1),
    reservationTypeAllowlist: z
        .array(z.enum(RESERVATION_TYPE_INTERNAL_KEY_VALUES))
        .nullable(),
})
    .strict();
export const createTaxSchema = z
    .object({
    name: taxText,
    guestFacingName: taxText,
    rate: taxRateSchema,
    taxCategory: taxText.nullable().default(null),
    applicability: taxApplicabilitySchema,
    applicableChannels: z.array(z.string().trim().min(1)).default([]),
    remitRentTaxToOwner: z.boolean().optional(),
    exemptionThresholdDays: z.number().int().min(1).nullable().optional(),
    distributionMethodOverride: z.enum(REVENUE_DISTRIBUTION_METHOD_VALUES).nullable().optional(),
})
    .strict();
export const updateTaxLabelsSchema = z
    .object({
    name: taxText.optional(),
    guestFacingName: taxText.optional(),
})
    .strict()
    .refine((o) => o.name !== undefined || o.guestFacingName !== undefined, {
    message: 'at_least_one_field',
});
export const updateTaxConfigSchema = z
    .object({
    rate: taxRateSchema.optional(),
    taxCategory: taxText.nullable().optional(),
    applicability: taxApplicabilitySchema.optional(),
    applicableChannels: z.array(z.string().trim().min(1)).optional(),
    remitRentTaxToOwner: z.boolean().optional(),
    exemptionThresholdDays: z.number().int().min(1).nullable().optional(),
    distributionMethodOverride: z.enum(REVENUE_DISTRIBUTION_METHOD_VALUES).nullable().optional(),
})
    .strict()
    .refine((o) => Object.keys(o).length > 0, { message: 'at_least_one_field' });
//# sourceMappingURL=tax.schema.js.map